import { OrgChartData } from '../types/orgchart.types';

// Import all org board JSON files
import ntamirData from './ntamir-orgboard-combined.json';
import ntamirNewData from './ntamir-new-orgboard.json';
import federmanData from './federman-orgboard.json';
import sternData from './stern-orgboard.json';
import fashionData from './fashion-orgboard.json';

export interface OrgBoardOption {
  id: string;
  name: string;
  data: OrgChartData;
}

// Helper to cast JSON to OrgChartData and migrate legacy data to 4-level hierarchy
const toOrgChartData = (data: any): OrgChartData => {
  // Migrate divisions: convert any dept.functions to dept.sections[0].functions
  const migratedDivisions: Record<string, any> = {};

  for (const [divKey, div] of Object.entries(data.divisions as Record<string, any>)) {
    const migratedDepartments = div.departments?.map((dept: any, deptIdx: number) => {
      // If department already has sections, keep them (but ensure functions array exists)
      if (dept.sections && dept.sections.length > 0) {
        return {
          ...dept,
          sections: dept.sections.map((s: any) => ({
            ...s,
            functions: s.functions || []
          }))
        };
      }

      // Migrate: wrap existing functions in a default section
      const legacyFunctions = dept.functions || [];
      const defaultSection = {
        id: `${divKey}-d${dept.number}-s0`,
        name: 'מדור',
        functions: legacyFunctions
      };

      // Remove legacy functions field, add sections
      const { functions, ...deptWithoutFunctions } = dept;
      return {
        ...deptWithoutFunctions,
        sections: [defaultSection]
      };
    }) || [];

    migratedDivisions[divKey] = {
      ...div,
      departments: migratedDepartments
    };
  }

  return {
    company: data.company,
    companyVfp: data.companyVfp,
    divisions: migratedDivisions,
    employees: data.employees || [],
    assignments: data.assignments,
  };
};

// All available org boards
export const orgBoards: OrgBoardOption[] = [
  {
    id: 'ntamir-new',
    name: 'נ.טמיר - חברת בניה (חדש)',
    data: toOrgChartData(ntamirNewData),
  },
  {
    id: 'ntamir',
    name: 'נ.טמיר - חברת בניה (ישן)',
    data: toOrgChartData(ntamirData),
  },
  {
    id: 'federman',
    name: 'פדרמן יועצים',
    data: toOrgChartData(federmanData),
  },
  {
    id: 'stern',
    name: 'שי נחשון - נדל"ן ובנייה',
    data: toOrgChartData(sternData),
  },
  {
    id: 'fashion',
    name: 'חברה לאופנה בע"מ',
    data: toOrgChartData(fashionData),
  },
];

// Get org board by ID
export const getOrgBoardById = (id: string): OrgChartData | undefined => {
  return orgBoards.find(ob => ob.id === id)?.data;
};

// Default org board (for demo/templates)
export const defaultOrgBoard = orgBoards[0];

// Empty org board template for new organizations - WISE Standard Structure
// Note: company name comes from organization.name in AuthContext, not stored here
// Uses 4-level hierarchy: Division → Department → Section → Function
export type OrgBoardLanguage = 'he' | 'en' | 'ar';

const emptyOrgBoardContent = {
  he: {
    companyVfp: 'מוצרים ושירותים איכותיים שנמסרו ללקוחות מרוצים ושנתקבלה התמורה המלאה עבורם',
    section: 'מדור',
    divisions: {
      hr: {
        name: 'אדמיניסטרציה וכוח אדם',
        vfp: 'עובדים מכובעים ופרודוקטיביים',
        departments: [
          { name: 'אתיקה ובקרה', number: 3, vfp: 'עובדים פרודקטביים ואתיים' },
          { name: 'קבלה ותקשורות', number: 2, vfp: 'שירותי משרד מעולים' },
          { name: 'כח אדם', number: 1, vfp: 'עובדים מכובעים בתפקידים הנדרשים בעלות מתאימה' },
        ]
      },
      sales: {
        name: 'שיווק ומכירות',
        vfp: 'לקוחות פוטנציאליים שפנו לקבלת הצעות מחיר',
        departments: [
          { name: 'שירות לקוחות', number: 6, vfp: 'לקוחות מטופלים ומרוצים' },
          { name: 'מכירות', number: 5, vfp: 'עסקאות שנסגרו עם לקוחות מרוצים' },
          { name: 'קידום ושיווק', number: 4, vfp: 'לקוחות פוטנציאליים כתוצאה ממסע פרסום אפקטיבי' },
        ]
      },
      finance: {
        name: 'הכספים',
        vfp: 'מקורות כספיים גדולים מהוצאות בתוספת עתודות',
        departments: [
          { name: 'בנקאות רשומות ונכסים', number: 9, vfp: 'רשומות מלאות ושלמות של הפעולות הפיננסיות' },
          { name: 'תשלומים', number: 8, vfp: 'ספקים מרוצים הנותנים שירות כנדרש, ניצול כספי נכון' },
          { name: 'מקורות כספיים', number: 7, vfp: 'מימון בכמות מספיקה ובעלות מינימלית, כספים שנגבו בזמן' },
        ]
      },
      operations: {
        name: 'תפעול והפקה',
        vfp: 'מוצרים ושירותים גמורים בזמן, בתקציב ובאיכות מעולה',
        departments: [
          { name: 'ביצוע', number: 12, vfp: 'תהליכי עבודה יעילים לפי תקן' },
          { name: 'הפקה וייצור', number: 11, vfp: 'מוצרים ושירותים שהושלמו בזמן ובאיכות מעולה' },
          { name: 'התקשרויות וציוד', number: 10, vfp: 'הסכמים עם ספקים וקבלנים במסגרת לו״ז ותקציב' },
        ]
      },
      quality: {
        name: 'בקרה ושירות לקוחות',
        vfp: 'מוצרים ושירותים שנמסרו ללקוחות מרוצים',
        departments: [
          { name: 'מסירות', number: 15, vfp: 'מוצרים ושירותים שנמסרו ללקוחות מרוצים' },
          { name: 'תיקונים ושיפורים', number: 14, vfp: 'מוצרים ושירותים שהותאמו לדרישות הלקוח' },
          { name: 'בקרה', number: 13, vfp: 'עמידה בתקנים ובאיכות' },
        ]
      },
      bizdev: {
        name: 'פיתוח עסקי ותכנון',
        vfp: 'הזדמנויות עסקיות חדשות עם תכניות מאושרות ובעלות פוטנציאל כלכלי',
        departments: [
          { name: 'תכנון ופיתוח', number: 18, vfp: 'תכניות סופיות מאושרות מתואמות וכלכליות' },
          { name: 'ניהול פרויקטים', number: 17, vfp: 'פרויקטים מלווים פיננסית ומנוהלים' },
          { name: 'פיתוח עסקי', number: 16, vfp: 'הזדמנויות עסקיות בעלות פוטנציאל כלכלי שנרכשו' },
        ]
      },
      executive: {
        name: 'המנהלים',
        vfp: 'חברה ריווחית שמתרחבת הפועלת לפי מדיניות',
        departments: [
          { name: 'משרד מנכ״ל', number: 19, vfp: 'חברה ריווחית העומדת ביעדים' },
          { name: 'המשרד לענייני חוץ', number: 20, vfp: 'מינימום פסקי דין נגד החברה, מקסימום פסקי דין לטובת החברה' },
          { name: 'משרד בעלים', number: 21, vfp: 'חברה שמשיגה את מוצריה עפ״י מדיניות' },
        ]
      },
    },
    executiveFunctions: [
      { id: 'exec-owner', name: 'בעלים' },
      { id: 'exec-ceo', name: 'מנהל ביצוע (ED)' },
    ]
  },
  en: {
    companyVfp: 'Quality products and services delivered to satisfied customers with full payment received',
    section: 'Section',
    divisions: {
      hr: {
        name: 'Administration & Human Resources',
        vfp: 'Trained and productive employees',
        departments: [
          { name: 'Ethics & Control', number: 3, vfp: 'Productive and ethical employees' },
          { name: 'Reception & Communications', number: 2, vfp: 'Excellent office services' },
          { name: 'Human Resources', number: 1, vfp: 'Trained employees in required positions at appropriate cost' },
        ]
      },
      sales: {
        name: 'Marketing & Sales',
        vfp: 'Potential customers requesting quotes',
        departments: [
          { name: 'Customer Service', number: 6, vfp: 'Satisfied and well-served customers' },
          { name: 'Sales', number: 5, vfp: 'Closed deals with satisfied customers' },
          { name: 'Marketing & Promotion', number: 4, vfp: 'Potential customers from effective advertising campaigns' },
        ]
      },
      finance: {
        name: 'Treasury',
        vfp: 'Income greater than expenses plus reserves',
        departments: [
          { name: 'Banking, Records & Assets', number: 9, vfp: 'Complete and accurate records of financial operations' },
          { name: 'Disbursements', number: 8, vfp: 'Satisfied vendors providing required service, proper financial utilization' },
          { name: 'Income', number: 7, vfp: 'Sufficient financing at minimal cost, funds collected on time' },
        ]
      },
      operations: {
        name: 'Operations & Production',
        vfp: 'Products and services completed on time, within budget, and with excellent quality',
        departments: [
          { name: 'Execution', number: 12, vfp: 'Efficient work processes according to standards' },
          { name: 'Production', number: 11, vfp: 'Products and services completed on time with excellent quality' },
          { name: 'Procurement & Equipment', number: 10, vfp: 'Agreements with vendors and contractors within schedule and budget' },
        ]
      },
      quality: {
        name: 'Quality Control & Customer Service',
        vfp: 'Products and services delivered to satisfied customers',
        departments: [
          { name: 'Delivery', number: 15, vfp: 'Products and services delivered to satisfied customers' },
          { name: 'Corrections & Improvements', number: 14, vfp: 'Products and services adapted to customer requirements' },
          { name: 'Quality Control', number: 13, vfp: 'Compliance with standards and quality' },
        ]
      },
      bizdev: {
        name: 'Business Development & Planning',
        vfp: 'New business opportunities with approved plans and economic potential',
        departments: [
          { name: 'Planning & Development', number: 18, vfp: 'Final approved plans that are coordinated and economical' },
          { name: 'Project Management', number: 17, vfp: 'Projects financially supported and managed' },
          { name: 'Business Development', number: 16, vfp: 'Business opportunities with economic potential acquired' },
        ]
      },
      executive: {
        name: 'Executive',
        vfp: 'A profitable and expanding company operating according to policy',
        departments: [
          { name: 'CEO Office', number: 19, vfp: 'A profitable company meeting its goals' },
          { name: 'External Affairs Office', number: 20, vfp: 'Minimum judgments against the company, maximum judgments in favor' },
          { name: 'Owner Office', number: 21, vfp: 'A company achieving its products according to policy' },
        ]
      },
    },
    executiveFunctions: [
      { id: 'exec-owner', name: 'Owner' },
      { id: 'exec-ceo', name: 'Executive Director (ED)' },
    ]
  },
  ar: {
    companyVfp: 'منتجات وخدمات عالية الجودة تم تسليمها للعملاء الراضين مع استلام الدفع الكامل',
    section: 'قسم',
    divisions: {
      hr: {
        name: 'الإدارة والموارد البشرية',
        vfp: 'موظفون مدربون ومنتجون',
        departments: [
          { name: 'الأخلاقيات والرقابة', number: 3, vfp: 'موظفون منتجون وأخلاقيون' },
          { name: 'الاستقبال والاتصالات', number: 2, vfp: 'خدمات مكتبية ممتازة' },
          { name: 'الموارد البشرية', number: 1, vfp: 'موظفون مدربون في المناصب المطلوبة بتكلفة مناسبة' },
        ]
      },
      sales: {
        name: 'التسويق والمبيعات',
        vfp: 'عملاء محتملون يطلبون عروض أسعار',
        departments: [
          { name: 'خدمة العملاء', number: 6, vfp: 'عملاء راضون ومخدومون جيداً' },
          { name: 'المبيعات', number: 5, vfp: 'صفقات مغلقة مع عملاء راضين' },
          { name: 'التسويق والترويج', number: 4, vfp: 'عملاء محتملون من حملات إعلانية فعالة' },
        ]
      },
      finance: {
        name: 'الخزينة',
        vfp: 'دخل أكبر من المصروفات بالإضافة إلى الاحتياطيات',
        departments: [
          { name: 'البنوك والسجلات والأصول', number: 9, vfp: 'سجلات كاملة ودقيقة للعمليات المالية' },
          { name: 'المدفوعات', number: 8, vfp: 'موردون راضون يقدمون الخدمة المطلوبة، استخدام مالي سليم' },
          { name: 'الدخل', number: 7, vfp: 'تمويل كافٍ بأقل تكلفة، أموال محصلة في الوقت المحدد' },
        ]
      },
      operations: {
        name: 'العمليات والإنتاج',
        vfp: 'منتجات وخدمات مكتملة في الوقت المحدد وضمن الميزانية وبجودة ممتازة',
        departments: [
          { name: 'التنفيذ', number: 12, vfp: 'عمليات عمل فعالة وفقاً للمعايير' },
          { name: 'الإنتاج', number: 11, vfp: 'منتجات وخدمات مكتملة في الوقت المحدد بجودة ممتازة' },
          { name: 'المشتريات والمعدات', number: 10, vfp: 'اتفاقيات مع الموردين والمقاولين ضمن الجدول الزمني والميزانية' },
        ]
      },
      quality: {
        name: 'مراقبة الجودة وخدمة العملاء',
        vfp: 'منتجات وخدمات تم تسليمها للعملاء الراضين',
        departments: [
          { name: 'التسليم', number: 15, vfp: 'منتجات وخدمات تم تسليمها للعملاء الراضين' },
          { name: 'التصحيحات والتحسينات', number: 14, vfp: 'منتجات وخدمات متكيفة مع متطلبات العملاء' },
          { name: 'مراقبة الجودة', number: 13, vfp: 'الامتثال للمعايير والجودة' },
        ]
      },
      bizdev: {
        name: 'تطوير الأعمال والتخطيط',
        vfp: 'فرص عمل جديدة مع خطط معتمدة وإمكانات اقتصادية',
        departments: [
          { name: 'التخطيط والتطوير', number: 18, vfp: 'خطط نهائية معتمدة ومنسقة واقتصادية' },
          { name: 'إدارة المشاريع', number: 17, vfp: 'مشاريع مدعومة مالياً ومُدارة' },
          { name: 'تطوير الأعمال', number: 16, vfp: 'فرص عمل ذات إمكانات اقتصادية تم الحصول عليها' },
        ]
      },
      executive: {
        name: 'الإدارة التنفيذية',
        vfp: 'شركة مربحة ومتوسعة تعمل وفقاً للسياسة',
        departments: [
          { name: 'مكتب المدير التنفيذي', number: 19, vfp: 'شركة مربحة تحقق أهدافها' },
          { name: 'مكتب الشؤون الخارجية', number: 20, vfp: 'أقل أحكام ضد الشركة، أقصى أحكام لصالح الشركة' },
          { name: 'مكتب المالك', number: 21, vfp: 'شركة تحقق منتجاتها وفقاً للسياسة' },
        ]
      },
    },
    executiveFunctions: [
      { id: 'exec-owner', name: 'المالك' },
      { id: 'exec-ceo', name: 'المدير التنفيذي (ED)' },
    ]
  }
};

// Division colors and icons (shared between languages)
const divisionMeta = {
  hr: { number: 1, color: '#e1c005', icon: '🏛️' },         // Establishment
  sales: { number: 2, color: '#c799ff', icon: '📈' },      // Marketing & Sales
  finance: { number: 3, color: '#ee9696', icon: '💰' },    // Treasury
  operations: { number: 4, color: '#6aba6a', icon: '🎯' }, // Delivery (Light Green)
  quality: { number: 5, color: '#adadad', icon: '📚' },    // Qualifications
  bizdev: { number: 6, color: '#e9ec36', icon: '🌐' },     // Public
  executive: { number: 7, color: '#87CEEB', icon: '👔' },  // Executive
};

export const createEmptyOrgBoard = (_companyName?: string, language: OrgBoardLanguage = 'he', customCompanyVfp?: string): OrgChartData => {
  const content = emptyOrgBoardContent[language];

  const divisions: Record<string, any> = {};
  for (const [key, meta] of Object.entries(divisionMeta)) {
    const divContent = content.divisions[key as keyof typeof content.divisions];
    divisions[key] = {
      name: divContent.name,
      number: meta.number,
      color: meta.color,
      icon: meta.icon,
      vfp: divContent.vfp,
      departments: divContent.departments.map((dept) => ({
        name: dept.name,
        number: dept.number,
        vfp: dept.vfp,
        ...(key === 'executive' && dept.number === 21 ? { managerId: 'owner' } : {}),
        sections: []
      }))
    };
  }

  return {
    companyVfp: customCompanyVfp || content.companyVfp,
    divisions,
    employees: [],
    executiveFunctions: [
      { ...content.executiveFunctions[0], managesDivisions: [] },
      { ...content.executiveFunctions[1], managesDivisions: ['executive', 'hr', 'sales', 'finance', 'operations', 'quality', 'bizdev'] },
    ],
    assignments: {}
  };
};
